import { FaqModalItem } from "@/components/site/FaqModalItem";
import { CheckCircle2, type LucideIcon } from "lucide-react";
import { serializeJsonLd } from "@/lib/jsonld";

export type FaqQA = { q: string; a: string };
export type BulletIcon = { Icon: LucideIcon; className: string };

interface SeoFaqBlockProps {
  title: string;
  intro?: string;
  bullets?: string[];
  /** Optional per-index colored circular icon badge for each bullet. */
  bulletIcons?: BulletIcon[];
  faqs: FaqQA[];
  /** When true, also injects FAQPage JSON-LD into the page head via a <script> tag. */
  emitJsonLd?: boolean;
}

/**
 * Reusable SEO content block: short intro, a bulleted checklist, and an FAQ accordion.
 * Used to expand thin pages with informative, professional copy and FAQ schema markup.
 */
export function SeoFaqBlock({ title, intro, bullets, bulletIcons, faqs, emitJsonLd = true }: SeoFaqBlockProps) {
  return (
    <section className="mt-20 border-t border-border/60 pt-14">
      <div className="max-w-3xl mx-auto">
        <h2 className="font-display text-2xl md:text-3xl font-bold text-center">{title}</h2>
        {intro && (
          <p className="mt-4 text-center text-muted-foreground leading-relaxed">{intro}</p>
        )}

        {bullets && bullets.length > 0 && (
          <ul className="mt-8 grid sm:grid-cols-2 gap-3">
            {bullets.map((b, i) => {
              const ic = bulletIcons?.[i];
              const Icon = ic?.Icon ?? CheckCircle2;
              const badgeCls = ic?.className ?? "bg-emerald-500 text-white";
              return (
                <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground leading-relaxed">
                  <span className={`${badgeCls} p-1.5 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    <Icon className="h-4 w-4" strokeWidth={2.25} />
                  </span>
                  <span className="pt-1">{b}</span>
                </li>
              );
            })}
          </ul>
        )}

        {faqs.length > 0 && (
          <div className="mt-10 glass rounded-3xl p-4 md:p-6 shadow-card border border-border/60">
            <div className="w-full divide-y divide-border/60">
              {faqs.map((f, i) => (
                <FaqModalItem
                  key={i}
                  question={f.q}
                  answer={f.a}
                  itemClassName="border-0 px-2 md:px-4"
                  triggerClassName="font-display font-semibold"
                />
              ))}
            </div>
          </div>
        )}

        {emitJsonLd && faqs.length > 0 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: serializeJsonLd({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              }),
            }}
          />
        )}
      </div>
    </section>
  );
}
